import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgRadioCellRendererComponent } from 'components/AgGrid/AgRadioCellRendererComponent';
import { numberFormatter } from 'components/AgGrid/utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { PriceListMtrRowData } from 'gql/graphql';
import { ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import {
    uerWorkOrderPriceListMtrAssignCodeFilterGql,
    uerWorkOrderPriceListMtrAssignNameFilterGql,
} from '../matchMtrDialogQueries';

export const labelMtrType = {
    [MtrTypeEnum.Equipment]: 'Оборудование',
    [MtrTypeEnum.Material]: 'Материал',
};

export const labelContragentType = {
    [ContragentTypeEnum.Contractor]: 'Подрядчик',
    [ContragentTypeEnum.Customer]: 'Заказчик',
};

interface IUseUerMtrRowsForComparison {
    contractId: number;
    mtrRowId?: number;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => void;
}

export const useUerMtrRowsForComparisonColumns = (props: IUseUerMtrRowsForComparison) => {
    const { contractId, mtrRowId, changeFilterHandler } = props;

    const filterVariables = useMemo(
        () => ({
            contractId,
            mtrRowId,
        }),
        [contractId, mtrRowId],
    );

    const [checkedMtrRowId, setCheckedMtrRowId] = useState<number>();

    const onCheckChange = useCallback((id: number, checked: boolean) => {
        setCheckedMtrRowId(checked ? id : undefined);
    }, []);

    const columns = useMemo<ColDef<PriceListMtrRowData>[]>(
        () => [
            {
                field: 'radio' as any,
                headerName: '',
                minWidth: 55,
                maxWidth: 55,
                cellRenderer: AgRadioCellRendererComponent,
                cellRendererParams: {
                    checkedId: checkedMtrRowId,
                    onCheckChange,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                minWidth: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'uerWorkOrderPriceListMtrAssignCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: uerWorkOrderPriceListMtrAssignCodeFilterGql,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                flex: 1,
                minWidth: 200,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'uerWorkOrderPriceListMtrAssignNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: uerWorkOrderPriceListMtrAssignNameFilterGql,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: 'unit',
                width: 100,
                minWidth: 80,
            },
            {
                field: 'unitCost',
                // eslint-disable-next-line react/display-name
                headerComponent: () => (
                    <div>
                        Стоимость единицы,
                        <div>руб.без НДС</div>
                    </div>
                ),
                headerClass: 'center-aligned-header breakWord-header',
                width: 183,
                minWidth: 120,
                valueFormatter: numberFormatter(2),
            },
            {
                headerName: 'Поставка',
                field: 'supplyBy',
                width: 142,
                minWidth: 80,
                resizable: true,
                valueFormatter: ({ data }) => (data?.supplyBy ? labelContragentType[data?.supplyBy] : ''),
            },
            {
                headerName: 'Тип',
                field: 'mtrType',
                width: 142,
                minWidth: 80,
                valueFormatter: ({ data }) => (data?.mtrType ? labelMtrType[data?.mtrType] : ''),
            },
        ],
        [checkedMtrRowId, onCheckChange, filterVariables, changeFilterHandler],
    );

    return {
        columns,
        checkedMtrRowId,
    };
};
